import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Text,
    TextButton,
    Flex,
    Spacer,
    Select,
} from '@zillow/constellation';

import { AppSelectors, AnalyticsSelectors } from 'app/shared/selectors';
import { getMonthDropdown } from 'app/shared/utils/dateUtils';
import { RMWContext } from 'app/shared/providers/RMWContext';
import AddressColumn from 'app/shared/components/Analytics/AddressColumn';
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import sharedConfig from 'app/shared/sharedConfig';
import stringHelper from 'app/shared/utils/stringHelper';
import TableMFH from 'app/shared/components/TableMFH';

const ANALYTICS_CATEGORY = 'Funnel Page';
const MONTH_DROPDOWN = getMonthDropdown();

const propTypes = {
    propertiesReport: PropTypes.object.isRequired,
};

const Error = ({ message }) => <Text fontColor="red400">{message}</Text>;

class FunnelPage extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        paths: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            startDate: MONTH_DROPDOWN[0].value,
        };

        this.handlePropertyClick = this.handlePropertyClick.bind(this);
        this.handleDownloadClick = this.handleDownloadClick.bind(this);
        this.handleReviewsClick = this.handleReviewsClick.bind(this);
        this.getPropertiesReportData = this.getPropertiesReportData.bind(this);
    }

    componentDidMount() {
        this.getPropertiesReportData(this.state.startDate);
    }

    handlePropertyClick(listingKey) {
        const { dispatch } = this.props;

        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.PROPERTY_COLUMN_CLICK, {
                category: ANALYTICS_CATEGORY,
            })
        );

        const { paths } = this.context;
        const url = `${paths.propertiesPage}/${listingKey}/${paths.propertyDetailsListing}`;
        window.router.transitionTo(url);
    }

    handleReviewsClick(listingKey) {
        const { dispatch } = this.props;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.REVIEW_COLUMN_CLICK));
        const { paths } = this.context;
        const url = `${paths.propertiesPage}/${listingKey}/${paths.propertyDetailsReviews}`;
        window.router.transitionTo(url);
    }

    handleDownloadClick() {
        const { startDate } = this.state;
        const { paths } = this.context;
        const excelPath = paths.exportExcel.properties;
        const startDateParam = startDate ? `?startDateYyyyMmDd=${startDate}` : '';
        const excelUrl = `${paths.proxy}/${sharedConfig.baseApi}${excelPath}${startDateParam}`;

        this.props.dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.DOWNLOAD_DATA_CLICK, {
            category: ANALYTICS_CATEGORY,
        }));
        window.open(excelUrl);
    }

    getPropertiesReportData(startDate) {
        const params = {
            startDateYyyyMmDd: startDate,
        };

        this.setState({
            isLoading: true,
            startDate,
        });
        this.props.dispatch(AnalyticsActions.getPropertiesReport(params))
            .then((result) => {
                const data = result.payload;
                if (data.success === false) {
                    this.setState({
                        isLoading: false,
                        error: 'Sorry, something went wrong. Please try again later.',
                    });
                } else {
                    this.setState({ isLoading: false });
                }
            });
    }

    getColumnConfig(isMobile, hasViewData, hasLeadData, hasLeaseData, hasClickData, hasDaysOnMarketData) {
        const columnConfig = [];

        if (hasViewData) {
            columnConfig.push({
                dataKey: 'totalViews',
                width: 1,
                header: 'Views',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasLeadData) {
            columnConfig.push({
                dataKey: 'totalLeads',
                width: 1,
                header: 'Contacts',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasDaysOnMarketData) {
            columnConfig.push({
                dataKey: 'totalDaysOnMarket',
                width: 1,
                header: 'Days on market',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasLeaseData && !isMobile) {
            columnConfig.push({
                dataKey: 'totalLeases',
                width: 1,
                header: 'Leases',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasClickData && !isMobile) {
            columnConfig.push({
                dataKey: 'totalClicks',
                width: 1,
                header: 'Website Referrals',
                align: 'right',
                isSortable: true,
            });
        }
        if (!isMobile) {
            columnConfig.push({
                dataKey: 'averageStarLevel',
                width: 1,
                header: 'Reviews',
                align: 'right',
                isSortable: true,
            });
        }
        const totalWidth = Math.ceil((columnConfig.length + 1) / 0.8);
        const headerColWidth = totalWidth - columnConfig.length;
        columnConfig.unshift({
            header: 'Advertising Type',
            width: 1,
            renderer: data => (data.productTypes.length > 0
                ? data.productTypes.map((productType, index) => (
                    <Text key={index} fontType="finePrint">{productType}</Text>
                ))
                : '-'),

        });
        columnConfig.unshift({
            dataKey: 'propertyName',
            sortType: 'string',
            width: headerColWidth,
            header: 'Property',
            isSortable: true,
            renderer: data => (
                <AddressColumn
                    index={data.index}
                    listing={data.listing}
                    listingKey={data.listingKey}
                    onClick={data.listing.hasAccess ? this.handlePropertyClick : undefined}
                    useAddress={data.totalDaysOnMarket > 0}
                />
            ),
        });
        return columnConfig;
    }

    getRowList(isMobile, propertiesReportModel, hasViewData, hasLeadData, hasLeaseData, hasClickData) {
        const rows = Object.values(propertiesReportModel.rows);
        const listings = propertiesReportModel.propertyDetails;
        return rows.map((data, index) => {
            const listingKey = `${data.feedListingId}_${data.feedId}`;
            const listing = listings[listingKey] || {};
            const rowData = {
                listingKey,
                listing,
                propertyName: data.totalDaysOnMarket > 0 ? listing.street : listing.name || listing.street,
                index,
            };

            rowData.totalViews = data.viewCount;
            rowData.totalLeads = data.leadCount;
            rowData.totalDaysOnMarket = data.daysOnMarketCount;

            if (hasLeaseData && !isMobile) {
                rowData.totalLeases = data.leaseCount;
            }
            if (hasClickData && !isMobile) {
                rowData.totalClicks = data.clickCount;
            }
            if (!isMobile) {
                rowData.averageStarLevel = data.averageStarLevel ?
                    (
                        <TextButton onClick={() => this.handleReviewsClick(listingKey)}>
                            {stringHelper.pluralize(data.averageStarLevel, 'star', 'stars')}
                        </TextButton>
                    ) :
                    '-';
            }
            rowData.productTypes = data.productTypes;
            return rowData;
        });
    }

    getTableData(propertiesReportModel, isMobile) {
        let columnConfig = [];
        let rowList = [];

        if (propertiesReportModel && propertiesReportModel.rows) {
            const hasLeadData = propertiesReportModel.leadCount > 0;
            const hasClickData = propertiesReportModel.clickCount > 0;
            const hasLeaseData = false;
            const hasViewData = propertiesReportModel.viewCount > 0;
            const hasDaysOnMarketData = propertiesReportModel.daysOnMarketCount > 0;

            columnConfig = this.getColumnConfig(isMobile, hasViewData, hasLeadData, hasLeaseData, hasClickData,
                hasDaysOnMarketData);
            rowList = this.getRowList(isMobile, propertiesReportModel, hasViewData, hasLeadData, hasLeaseData,
                hasClickData);
        }

        return {
            rowList,
            columnConfig,
        };
    }

    render() {
        const { isLoading, error } = this.state;
        const { propertiesReport, isScreenMobile } = this.props;

        const {
            rowList,
            columnConfig,
        } = this.getTableData(propertiesReport, isScreenMobile);

        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="FunnelPage">
                    <HeadWrapper title="Funnel Analytics" />
                    <Spacer marginY="sm">
                        <Flex display="flex" justifyContent="space-between">
                            <Select
                                disabled={isLoading}
                                name="startDate"
                                onChange={e => this.getPropertiesReportData(e.target.value)}
                                fluid={false}
                            >
                                {MONTH_DROPDOWN.map(({ value, name }) => (
                                    <option
                                        key={value}
                                        value={value}
                                    >
                                        {name}
                                    </option>
                                ))}
                            </Select>
                            {!isLoading && (
                                <TextButton onClick={this.handleDownloadClick}>
                                    Export details
                                </TextButton>
                            )}
                        </Flex>
                    </Spacer>
                    <TableMFH
                        idKey="funnel"
                        columnConfig={columnConfig}
                        data={isLoading ? [] : rowList}
                        ErrorRowComponent={error ? () => <Error message={error} /> : undefined}
                        isLoading={isLoading}
                        columnDivisions={columnConfig.length ? (columnConfig[0].width + columnConfig.length - 1) : 1}
                        defaultSortKey={{
                            key: 'propertyName',
                            ascending: true,
                        }}
                    />
                    {(!isLoading && rowList.length === 0 && !error) &&
                        <Error message="No properties found." />
                    }
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    propertiesReport: AnalyticsSelectors.makePropertiesReport(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
});

FunnelPage.propTypes = propTypes;

export { FunnelPage };
export default connect(mapStateToProps)(FunnelPage);
